import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { DRAFT_ROW_KEY } from '@routes/sidebar/admin-center/___shared___/constants.ts';
import {
    MATRIX_PATHWAY_CATEGORY,
    MATRIX_PATHWAY_TRIGGER,
    MATRIX_PATHWAY_TYPE,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/constants.ts';
import type { MatricesRow } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/table/types.ts';

export const MATRICES_DATA_INDEX = {
    ID: 'id',
    LR_ID: 'lrId',
    PATHWAY_ID: 'pathwayId',
    PATHWAY_NAME: 'pathwayName',
    ADAPTIVE_PRE_TEST: 'adaptivePreTest',
    REQUIREMENT_TYPE: 'requirementType',
    SELECTOR_BY_VESSEL_GROUP: 'selector',
    RANKS_BY_RANKS_VESSEL_GROUP: 'ranks',
    LEARNING_CATEGORY: 'learningCategory',
    TRIGGER: 'trigger',
    INITIAL_DUE_VALUE: 'dueValue',
    INITIAL_DUE_FORMAT: 'dueFormat',
    DUE_NOTICE_VALUE: 'dueNoticeValue',
    DUE_NOTICE_FORMAT: 'dueNoticeFormat',
    REFRESH: 'refresh',
    REFRESH_PERIOD_VALUE: 'refreshPeriodValue',
    REFRESH_PERIOD_FORMAT: 'refreshPeriodFormat',
    STATUS: 'status',
    IS_ACTIVE: 'isActive',
    CAN_ENABLE_ADAPTIVE_PRE_TEST: 'canEnableAdaptivePreTest',

    // keep it but not used
    CREATED_AT_UTC: 'createdAtUtc',
    UPDATED_AT_UTC: 'updatedAtUtc',
    CATEGORY_FOCUS: 'categoryFocus',
    HAS_PRACTICE_QUIZ: 'hasPracticeQuiz',
} as const;

export const MATRICES_COLUMN_TITLE = {
    LR_ID: msg`ID`,
    PATHWAY_NAME: msg`Pathway`,
    ADAPTIVE_TEST_STATUS: msg`Adaptive pre-test`,
    REQUIREMENT_TYPE: msg`Requirement Type*`,
    SELECTOR_BY_VESSEL_GROUP: msg`Selector`,
    RANKS_BY_RANKS_VESSEL_GROUP: msg`Ranks*`,
    LEARNING_CATEGORY: msg`Category*`,
    TRIGGER: msg`Start Trigger`,
    INITIAL_DUE_VALUE: msg`Initial Due`,
    DUE_NOTICE_VALUE: msg`Due Notice`,
    REFRESH: msg`Refresh`,
    REFRESH_PERIOD_VALUE: msg`Refresh Period`,
    STATUS: msg`Status`,
    IS_ACTIVE: msg`Active`,
} as const;

// TODO: Update these to match backend field names
export const MATRICES_ENTITY_TYPE = 'RequirementMatrix' as const;
export const MATRICES_VALIDATION_FIELDS_NAMES = {
    REQUIREMENT_TYPE: 'requirementType',
} as const;

export const MATRICES_COLUMN_SORT_MAP: Record<string, string> = {
    [MATRICES_DATA_INDEX.LR_ID]: MATRICES_DATA_INDEX.LR_ID,
    [MATRICES_DATA_INDEX.PATHWAY_NAME]: MATRICES_DATA_INDEX.PATHWAY_NAME,
    [MATRICES_DATA_INDEX.REQUIREMENT_TYPE]: MATRICES_DATA_INDEX.REQUIREMENT_TYPE,
};
export const MATRICES_COLUMN_FILTER_MAP: Record<string, string> = {
    [MATRICES_DATA_INDEX.REQUIREMENT_TYPE]: MATRICES_DATA_INDEX.REQUIREMENT_TYPE,
    [MATRICES_DATA_INDEX.LEARNING_CATEGORY]: MATRICES_DATA_INDEX.LEARNING_CATEGORY,
    [MATRICES_DATA_INDEX.TRIGGER]: MATRICES_DATA_INDEX.TRIGGER,
    [MATRICES_DATA_INDEX.STATUS]: MATRICES_DATA_INDEX.STATUS,
};

export const MATRICES_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [MATRICES_DATA_INDEX.LR_ID]: MATRICES_COLUMN_TITLE.LR_ID,
    [MATRICES_DATA_INDEX.PATHWAY_NAME]: MATRICES_COLUMN_TITLE.PATHWAY_NAME,
    [MATRICES_DATA_INDEX.STATUS]: MATRICES_COLUMN_TITLE.STATUS,
    [MATRICES_DATA_INDEX.IS_ACTIVE]: MATRICES_COLUMN_TITLE.IS_ACTIVE,
    [MATRICES_DATA_INDEX.REQUIREMENT_TYPE]: MATRICES_COLUMN_TITLE.REQUIREMENT_TYPE,
    [MATRICES_DATA_INDEX.LEARNING_CATEGORY]: MATRICES_COLUMN_TITLE.LEARNING_CATEGORY,
    [MATRICES_DATA_INDEX.TRIGGER]: MATRICES_COLUMN_TITLE.TRIGGER,
    [MATRICES_DATA_INDEX.ADAPTIVE_PRE_TEST]: MATRICES_COLUMN_TITLE.ADAPTIVE_TEST_STATUS,
    [MATRICES_DATA_INDEX.SELECTOR_BY_VESSEL_GROUP]: MATRICES_COLUMN_TITLE.SELECTOR_BY_VESSEL_GROUP,
    [MATRICES_DATA_INDEX.RANKS_BY_RANKS_VESSEL_GROUP]: MATRICES_COLUMN_TITLE.RANKS_BY_RANKS_VESSEL_GROUP,
    [MATRICES_DATA_INDEX.INITIAL_DUE_VALUE]: MATRICES_COLUMN_TITLE.INITIAL_DUE_VALUE,
    [MATRICES_DATA_INDEX.DUE_NOTICE_VALUE]: MATRICES_COLUMN_TITLE.DUE_NOTICE_VALUE,
    [MATRICES_DATA_INDEX.REFRESH]: MATRICES_COLUMN_TITLE.REFRESH,
    [MATRICES_DATA_INDEX.REFRESH_PERIOD_VALUE]: MATRICES_COLUMN_TITLE.REFRESH_PERIOD_VALUE,
};

export const MATRICES_DATASOURCE_REFS = {
    REQUIREMENT_TYPES: 'RefRequirementTypes',
    SELECTOR_BY_VESSEL_GROUP: 'RefSelectorByVesselGroup', // custom multi-select
    RANKS_BY_RANKS_VESSEL_GROUP: 'RefRanksByRanksAndVesselGroup', // custom multi-select
    LEARNING_CATEGORIES: 'RefLearningCategories',
    TRIGGERS: 'RefTriggers',
    REFRESH: 'RefRefresh',
    INITIAL_DUE_TIME_UNITS: 'RefInitialDueTimeUnits',
    DUE_NOTICE_TIME_UNITS: 'RefDueNoticeTimeUnits',
    REFRESH_PERIOD_TIME_UNITS: 'RefRefreshPeriodTimeUnits',
    STATUS: 'RefStatus',
};

export const EDIT_MATRIX_REQUIREMENT_KEY = 'edit-matrix-requirement';
export const DUPLICATE_MATRIX_REQUIREMENT_KEY = 'duplicate-matrix-requirement';
export const REVERT_TO_DEFAULT_MATRIX_REQUIREMENT_KEY = 'revert-to-default-matrix-requirement';
export const DELETE_DUPLICATE_MATRIX_REQUIREMENT_KEY = 'delete-duplicate-matrix-requirement';

// selectors constants
const REQUIREMENT_TYPES_DATA = {
    BY_RANK_ONLY_LABEL: msg`By rank only`,
    BY_RANK_ONLY_SUB_LABEL: msg`Applies to ranks only, no vessel filter`,
    BY_RANK_VESSEL_GROUP_LABEL: msg`By vessel group`,
    BY_RANK_VESSEL_GROUP_SUB_LABEL: msg`Applies to selected vessels groups, types, or sub-types defined by the tenant.`,
};

const CATEGORY_DATA = {
    MANDATORY_LABEL: msg`Mandatory`,
    RECOMMENDED_LABEL: msg`Recommended`,
};

const TRIGGER_DATA = {
    SINCE_REQUIREMENT_START_LABEL: msg`Since requirement start`,
    SINCE_REQUIREMENT_START_SUB_LABEL: msg`Starts from the later of the crew's requirement start date, the Learning Requirement first activation date, or change of rank date (if rank changed after the requirement was activated).`,
    SINCE_VESSEL_ASSIGNMENT_LABEL: msg`Since vessel assignment`,
    SINCE_VESSEL_ASSIGNMENT_SUB_LABEL: msg`Starts from the later of the Learning Requirement first activation date, vessel assignment date, or change of rank date if the rank changes onboard`,
    AFTER_SIGN_ON_LABEL: msg`After sign on`,
    AFTER_SIGN_ON_SUB_LABEL: msg`Starts from the later of the Learning Requirement first activation date or the sign-on-based trigger date.`,
};

const REFRESH_DATA = {
    YES: msg`Yes`,
    NO: msg`No`,
};

const TIME_UNITS_DATA = {
    TWENTY_FOUR_HOURS: msg`24 hours`,
    FORTY_EIGHT_HOURS: msg`48 hours`,
    SEVENTY_TWO_HOURS: msg`72 hours`,
    ONE_WEEK: msg`1 week`,
    TWO_WEEKS: msg`2 weeks`,
    ONE_MONTH: msg`1 month`,
    TWO_MONTHS: msg`2 months`,
    THREE_MONTHS: msg`3 months`,
    SIX_MONTHS: msg`6 months`,
    ONE_YEAR: msg`1 year`,
    TWO_YEARS: msg`2 years`,
    THREE_YEARS: msg`3 years`,
    FOUR_YEARS: msg`4 years`,
    FIVE_YEARS: msg`5 years`,
};

export const REQUIREMENT_TYPES_OPTIONS = [
    {
        value: MATRIX_PATHWAY_TYPE.ByRankOnly,
        label: REQUIREMENT_TYPES_DATA.BY_RANK_ONLY_LABEL,
        subLabel: REQUIREMENT_TYPES_DATA.BY_RANK_ONLY_SUB_LABEL,
    },
    {
        value: MATRIX_PATHWAY_TYPE.ByVesselGroup,
        label: REQUIREMENT_TYPES_DATA.BY_RANK_VESSEL_GROUP_LABEL,
        subLabel: REQUIREMENT_TYPES_DATA.BY_RANK_VESSEL_GROUP_SUB_LABEL,
    },
];

export const CATEGORY_OPTIONS = [
    {
        value: MATRIX_PATHWAY_CATEGORY.Mandatory,
        label: CATEGORY_DATA.MANDATORY_LABEL,
    },
    {
        value: MATRIX_PATHWAY_CATEGORY.Recommended,
        label: CATEGORY_DATA.RECOMMENDED_LABEL,
    },
];

export const TRIGGER_OPTIONS = [
    {
        value: MATRIX_PATHWAY_TRIGGER.SinceRequirementStart,
        label: TRIGGER_DATA.SINCE_REQUIREMENT_START_LABEL,
        subLabel: TRIGGER_DATA.SINCE_REQUIREMENT_START_SUB_LABEL,
    },
    {
        value: MATRIX_PATHWAY_TRIGGER.AfterSignOn,
        label: TRIGGER_DATA.AFTER_SIGN_ON_LABEL,
        subLabel: TRIGGER_DATA.AFTER_SIGN_ON_SUB_LABEL,
    },
    {
        value: MATRIX_PATHWAY_TRIGGER.SinceVesselAssignment,
        label: TRIGGER_DATA.SINCE_VESSEL_ASSIGNMENT_LABEL,
        subLabel: TRIGGER_DATA.SINCE_VESSEL_ASSIGNMENT_SUB_LABEL,
    },
];

export const REFRESH_OPTIONS = [
    {
        value: 'true',
        label: REFRESH_DATA.YES,
    },
    {
        value: 'false',
        label: REFRESH_DATA.NO,
    },
];

export const INITIAL_DUE_OPTIONS = [
    { value: '48|Hour', label: TIME_UNITS_DATA.FORTY_EIGHT_HOURS },
    { value: '72|Hour', label: TIME_UNITS_DATA.SEVENTY_TWO_HOURS },
    { value: '1|Week', label: TIME_UNITS_DATA.ONE_WEEK },
    { value: '2|Week', label: TIME_UNITS_DATA.TWO_WEEKS },
    { value: '1|Month', label: TIME_UNITS_DATA.ONE_MONTH },
    { value: '2|Month', label: TIME_UNITS_DATA.TWO_MONTHS },
    { value: '3|Month', label: TIME_UNITS_DATA.THREE_MONTHS },
    { value: '6|Month', label: TIME_UNITS_DATA.SIX_MONTHS },
    { value: '1|Year', label: TIME_UNITS_DATA.ONE_YEAR },
    { value: '2|Year', label: TIME_UNITS_DATA.TWO_YEARS },
    { value: '3|Year', label: TIME_UNITS_DATA.THREE_YEARS },
    { value: '4|Year', label: TIME_UNITS_DATA.FOUR_YEARS },
    { value: '5|Year', label: TIME_UNITS_DATA.FIVE_YEARS },
];

export const TIME_UNITS_OPTIONS = [
    { value: '24|Hour', label: TIME_UNITS_DATA.TWENTY_FOUR_HOURS },
    ...INITIAL_DUE_OPTIONS,
];

// selectors group names

//vessels
export const VESSEL_GROUP = 'VESSEL GROUP';
export const VESSEL_TYPE = 'VESSEL TYPE';

//ranks
export const DEPARTMENT = 'DEPARTMENT';
export const LEVEL = 'LEVEL';

export const buildEmptyMatrixDraftRow = (): MatricesRow => ({
    key: String(DRAFT_ROW_KEY),
    [MATRICES_DATA_INDEX.ID]: null,
    [MATRICES_DATA_INDEX.LR_ID]: null,
    [MATRICES_DATA_INDEX.PATHWAY_ID]: null,
    [MATRICES_DATA_INDEX.PATHWAY_NAME]: null,
    [MATRICES_DATA_INDEX.ADAPTIVE_PRE_TEST]: false,
    [MATRICES_DATA_INDEX.REQUIREMENT_TYPE]: null,
    [MATRICES_DATA_INDEX.LEARNING_CATEGORY]: null,
    [MATRICES_DATA_INDEX.TRIGGER]: null,
    [MATRICES_DATA_INDEX.SELECTOR_BY_VESSEL_GROUP]: [],
    [MATRICES_DATA_INDEX.RANKS_BY_RANKS_VESSEL_GROUP]: [],
    [MATRICES_DATA_INDEX.INITIAL_DUE_VALUE]: null,
    [MATRICES_DATA_INDEX.DUE_NOTICE_VALUE]: null,
    [MATRICES_DATA_INDEX.REFRESH]: false,
    [MATRICES_DATA_INDEX.REFRESH_PERIOD_VALUE]: null,
    [MATRICES_DATA_INDEX.STATUS]: null,
    [MATRICES_DATA_INDEX.IS_ACTIVE]: false,
    [MATRICES_DATA_INDEX.CATEGORY_FOCUS]: null,
    [MATRICES_DATA_INDEX.HAS_PRACTICE_QUIZ]: false,
    [MATRICES_DATA_INDEX.CAN_ENABLE_ADAPTIVE_PRE_TEST]: false,
});
